import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Marker, useMap, useMapEvents } from 'react-leaflet';
import LoadingContext from '../../context/loadingContext';
import { TornadoMarker, TornadoService } from '../../services/tornado/tornadoService';
import "./Tornado.css";
import TornadoContent from './TornadoContent';
import TornadoDialog from './TornadoDialog';

const Tornado = () => {
    const [markers, setMarkers] = useState<null | TornadoMarker[]>(null);
    const setLoading = useContext(LoadingContext);
    const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
    const map = useMap();
    const tornadoService = useMemo(() => new TornadoService(map), [map]);
    const markerRefs = useRef<Map<string, L.Marker>>(new Map());

    const fetchMarkers = useCallback(() => {
        const markers = tornadoService.getMarkers();
        setMarkers(markers);
    }, [tornadoService]);

    useMapEvents({
        zoom: () => fetchMarkers(),
        move: () => fetchMarkers(),
    });

    useEffect(() => {
        const handleDataUpdate = (newMarkers: TornadoMarker[]) => {
            setMarkers(newMarkers)
            setLoading(false);
        }

        const initializeService = async () => {
            setLoading(true);
            try {
                await tornadoService.init(handleDataUpdate);
            } catch (error) {
                console.error("Failed to initialize tornado service:", error);
                setLoading(false);
            }
        }

        initializeService();
        return () => {
            tornadoService.cleanup();
        }
    }, [tornadoService]);

    const setMarkerRef = useCallback((index: string, marker: L.Marker | null) => {
        if (marker) {
            markerRefs.current.set(index, marker);
        } else {
            markerRefs.current.delete(index);
        }
    }, []);

    return (
        <>
            {
                markers && markers.map((marker: TornadoMarker, index: number) => {

                    return (
                        <div key={marker.id}>
                            <Marker
                                position={marker.pinpoint}
                                icon={marker.icon}
                                pane="overlayMarkersPane"
                                eventHandlers={{
                                    mouseover: (e) => {
                                        setActiveMarkerId(marker.id);
                                    },
                                    mouseout: (e) => {
                                        setActiveMarkerId(null);
                                    },
                                }}
                                ref={(ref) => setMarkerRef(marker.id, ref)}
                            >
                            </Marker>
                            {markerRefs.current.get(marker.id) && (
                                <TornadoDialog
                                    marker={markerRefs.current.get(marker.id)!}
                                    isOpen={activeMarkerId === marker.id}
                                    onClose={() => setActiveMarkerId(null)}
                                >
                                    {(dialogRef) => <TornadoContent tornado={marker} dialogRef={dialogRef} />}
                                </TornadoDialog>
                            )}
                        </div>
                    )
                })
            }
        </>
    );
}

export default Tornado;
